import { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { type GitlabProvisioningManageResult } from 'network/httpClients/GitlabClient';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { useGitlabInstallationV2 } from '../gitlabInstallationV2Context';
import {
  getResultLabel,
  GitlabManageResults,
  resolveInstallStartErrorKey,
  useGitlabManageRun,
} from '../manageRun';
import { getInstallErrorKey } from '../utils';

/**
 * Single-install step — self-managed and legacy single-group cloud, where the bot was
 * provisioned on Connect. On entry it starts the async install (webhooks + bot
 * membership) for the selection carried from Select against the one `installationId`,
 * then polls until terminal via the shared `useGitlabManageRun` lifecycle. The banner
 * is derived from the per-scope `results` (not the top-level status alone): all
 * succeeded → success, some failed → partial, none succeeded → failed. A single Retry
 * re-runs the whole install in place (already-installed scopes come back skipped). Back
 * is blocked on this step (see configuration.ts). "Done" is enabled once at least one
 * scope is installed (success or partial). Presentation is the shared `GitlabManageResults`.
 */
export const SingleGroupInstallResults = () => {
  const { setIsNextEnabled, setIsPending } = useMultiStepDialog();
  const { accessToken, installationId, installSelection, scopePaths } =
    useGitlabInstallationV2();

  const spawnInstall = spawn('dialogs.gitlab-installation-v2.steps.install');
  const spawnErrors = spawn(
    'dialogs.gitlab-installation-v2.steps.install.errors',
  );
  const spawnItemStatus = spawn(
    'dialogs.gitlab-installation-v2.steps.install.item-status',
  );

  const canStartInstall =
    accessToken !== undefined &&
    installationId !== undefined &&
    installSelection !== undefined;

  const runParams =
    accessToken !== undefined && installationId !== undefined
      ? { installationId, accessToken }
      : undefined;

  const {
    run,
    retry,
    results,
    succeededResults,
    outcome,
    transportErrorKey,
    wholeRunErrorCode,
  } = useGitlabManageRun(
    runParams,
    resolveInstallStartErrorKey(getInstallErrorKey),
  );

  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (
      hasStartedRef.current ||
      !canStartInstall ||
      installSelection === undefined
    )
      return;

    hasStartedRef.current = true;
    // Install is add-only: the selection goes under `add`, `remove` is empty.
    run({
      add: {
        groupIds: installSelection.groupIds,
        projectIds: installSelection.projectIds,
      },
      remove: { groupIds: [], projectIds: [] },
    });
  }, [canStartInstall, installSelection, run]);

  const isPending = outcome === 'pending';
  const canProceed = outcome === 'success' || outcome === 'partial';
  const canRetry = outcome === 'failed' || outcome === 'partial';

  useEffect(() => {
    setIsNextEnabled(canProceed);
  }, [canProceed]);

  useEffect(() => {
    setIsPending(isPending);
  }, [isPending]);

  const itemStatusLabel = (result: GitlabProvisioningManageResult) =>
    result.status === 'failed' && result.error
      ? spawnErrors(getInstallErrorKey(result.error.code))
      : spawnItemStatus(result.status);

  const bannerStatus =
    outcome === 'success'
      ? 'success'
      : outcome === 'partial'
        ? 'warning'
        : 'error';

  const getBannerMessage = () => {
    if (outcome === 'success') return spawnInstall('done');

    if (outcome === 'partial') {
      return spawnInstall('partial', {
        success: succeededResults.length,
        total: results.length,
      });
    }

    if (transportErrorKey !== undefined) return spawnErrors(transportErrorKey);

    if (results.length > 0) return spawnInstall('all-failed');

    return spawnErrors(getInstallErrorKey(wholeRunErrorCode));
  };

  const groupCount = installSelection?.groupIds.length ?? 0;
  const projectCount = installSelection?.projectIds.length ?? 0;
  const summaryParts = [
    groupCount > 0
      ? spawnInstall('summary-groups', { count: groupCount })
      : undefined,
    projectCount > 0
      ? spawnInstall('summary-projects', { count: projectCount })
      : undefined,
  ].filter((part): part is string => part !== undefined);
  const pendingDescription =
    summaryParts.length > 0
      ? spawnInstall('summary', {
          scopes: summaryParts.join(spawnInstall('summary-join')),
        })
      : spawnInstall('description');

  // A refresh or deep-link onto this step loses the in-memory wizard context
  // (PAT, installation id, selection); the PAT can't be recovered, so restart
  // the wizard instead of leaving the user on an endless pending loader.
  if (!canStartInstall) {
    return (
      <Navigate
        to={{
          pathname: ROUTES.GITLAB_V2_CONNECT,
          search: window.location.search,
        }}
        replace
      />
    );
  }

  return (
    <Step title={spawnInstall('title')}>
      <GitlabManageResults
        isPending={isPending}
        pendingMessage={pendingDescription}
        bannerStatus={bannerStatus}
        bannerMessage={getBannerMessage()}
        retryLabel={canRetry ? spawnInstall('retry-label') : undefined}
        onRetry={canRetry ? retry : undefined}
        results={results}
        showKindIcon
        getResultStatusLabel={itemStatusLabel}
        getResultLabel={(result) =>
          getResultLabel(
            result,
            scopePaths.groupPathsById,
            scopePaths.projectPathsById,
          )
        }
      />
    </Step>
  );
};
